import { z } from "zod";
import { isNationalIdValid } from "@/lib";
import { phoneNumberValidation } from "./auth.schema";

const cardInputValidation = z.string().length(4).regex(/^\d+$/);

export const generateDigit = (
  length: number,
  message = "validation.default",
) => {
  // Ensure missing values (undefined) produce a localized message instead of the default English "Required"
  return z
    .string({ required_error: "validation.default" })
    .length(length, message)
    .regex(/^\d+$/);
};

export const creditCardSchema = z
  .object({
    input1: cardInputValidation,
    input2: cardInputValidation,
    input3: cardInputValidation,
    input4: cardInputValidation,
  })
  .refine((data) => {
    const fullCard = Object.values(data).join("");
    return fullCard.length === 16;
  });

export const ibanCardSchema = z
  .object({
    input1: cardInputValidation,
    input2: cardInputValidation,
    input3: cardInputValidation,
    input4: cardInputValidation,
    input5: cardInputValidation,
    input6: cardInputValidation,
  })
  .refine((data) => {
    const fullCard = Object.values(data).join("");
    return fullCard.length === 24;
  });

export const creditIncrementSchema = z.object({
  // packageId: z.string(),
  bank: z.string().optional(),
  digit: z
    .string()
    .min(1, "validation.required.creditIncrement")
    .max(9, "validation.invalid.creditIncrementMaximum")
    .regex(/^\d+$/, "validation.invalid.creditIncrementNumeric")
    .refine((value) => parseInt(value, 10) >= 1000, {
      message: "validation.invalid.creditIncrementMinimum",
    }),
});

export const creditPackageSchema = z.object({
  packageId: z.string().min(1, "validation.required.packageId"),
});

export const purchasePackageRequest = z.object({
  amount: z.number() || z.string(),
  use_wallet: z.boolean(),
  callback: z.string(),
  items: z.any(),
});

export const creditIncrementSchemaCombinePackage = z.object({
  amount: z.number() || z.string(),
  use_wallet: z.boolean(),
  callback: z.string(),
  items: z.any().optional(),
});

export const payFines = z.object({
  inquirePrice: z.string(),
  paperId: z.number(),
  paymentId: z.number(),
});

export const militaryServiceInquirySchema = z.object({
  digit: generateDigit(10),
  birth_date: z.string(),
});

export const codePostiServiceInquirySchema = z.object({
  digit: generateDigit(10),
});

export const companyIdentificationNumberSchema = z.object({
  digit: generateDigit(11),
});

export const accountNumberSchema = z.object({
  number: z.string().min(1, "validation.required.accountNumber"),
  bank: z.string().min(1, "validation.required.bank"),
});

export const bouncedCheckInquirySchema = z
  .object({
    nationalCode: z.string().optional(),
    nationalId: z.string().optional(),
    role: z.string().min(1),
  })
  .refine(
    (data) => {
      // For individual (role "1"), nationalCode is required and must be 10 digits
      if (data.role === "1") {
        // Only validate if user has entered something
        if (!data.nationalCode || data.nationalCode.trim() === "") {
          return true; // Don't show error for empty field initially
        }
        return (
          data.nationalCode.length === 10 && /^\d+$/.test(data.nationalCode)
        );
      }
      // For legal entity (role "2"), nationalId is required and must be 10 digits
      if (data.role === "2") {
        // Only validate if user has entered something
        if (!data.nationalId || data.nationalId.trim() === "") {
          return true; // Don't show error for empty field initially
        }
        return data.nationalId.length === 10 && /^\d+$/.test(data.nationalId);
      }
      return true;
    },
    {
      message: "validation.nationalCode",
      path: ["nationalCode"],
    },
  )
  .refine(
    (data) => {
      // For legal entity (role "2"), nationalId is required and must be 10 digits
      if (data.role === "2") {
        // Only validate if user has entered something
        if (!data.nationalId || data.nationalId.trim() === "") {
          return true; // Don't show error for empty field initially
        }
        return data.nationalId.length === 10 && /^\d+$/.test(data.nationalId);
      }
      return true;
    },
    {
      message: "validation.nationalId",
      path: ["nationalId"],
    },
  );
export const postalCodeSchema = z.object({
  digit: generateDigit(10),
});

export const khalafiSchema = z
  .object({
    national_code: generateDigit(10, "validation.default").refine(
      isNationalIdValid,
      { message: "validation.nationalCode" },
    ),
    plk1: generateDigit(2, "validation.provinceCode"),
    plk2: z.string(),
    plk3: generateDigit(3, "validation.serial"),
    plk_serial: generateDigit(2, "validation.province"),
  })
  .refine(
    (data) =>
      data.plk1.length === 2 &&
      data.plk3.length === 3 &&
      data.plk_serial.length === 2,
    {
      message: "پلاک باید ۸ رقم باشد",
    },
  );

export const vehicleTransferTaxInquirySchema = z
  .object({
    national_code: generateDigit(10, "validation.default").refine(
      isNationalIdValid,
      { message: "validation.nationalCode" },
    ),
    plk1: generateDigit(2, "validation.provinceCode"),
    plk2: z.string(),
    plk3: generateDigit(3, "validation.serial"),
    plk_serial: generateDigit(2, "validation.province"),
    birth_date: z.string().min(1, "validation.default"),
  })
  .refine(
    (data) =>
      data.plk1.length === 2 &&
      data.plk3.length === 3 &&
      data.plk_serial.length === 2,
    {
      message: "validation.plateNoNotValid",
      path: ["plk3"],
    },
  );

export const freewayTollInquirySchema = z
  .object({
    plk1: generateDigit(2, "validation.provinceCode"),
    plk2: z.string(),
    plk3: generateDigit(3, "validation.serial"),
    plk_serial: generateDigit(2, "validation.province"),
  })
  .refine(
    (data) =>
      data.plk1.length === 2 &&
      data.plk3.length === 3 &&
      data.plk_serial.length === 2,
    {
      message: "validation.plateNoNotValid",
      path: ["plk3"],
    },
  );

export const vehicleInfoInquirySchema = z
  .object({
    national_code: generateDigit(10, "validation.default").refine(
      isNationalIdValid,
      { message: "validation.nationalCode" },
    ),
    plk1: generateDigit(2, "validation.provinceCode"),
    plk2: z.string(),
    plk3: generateDigit(3, "validation.serial"),
    plk_serial: generateDigit(2, "validation.province"),
  })
  .refine(
    (data) =>
      data.plk1.length === 2 &&
      data.plk3.length === 3 &&
      data.plk_serial.length === 2,
    {
      message: "validation.plateNoNotValid",
      path: ["plk3"],
    },
  );

export const thirdPartyInsuranceInquirySchema = vehicleInfoInquirySchema;

export const carDocumentSchema = z
  .object({
    national_code: generateDigit(10, "validation.default").refine(
      isNationalIdValid,
      { message: "validation.nationalCode" },
    ),
    plk1: generateDigit(2, "validation.provinceCode"),
    plk2: z.string(),
    plk3: generateDigit(3, "validation.serial"),
    plk_serial: generateDigit(2, "validation.province"),
  })
  .refine(
    (data) =>
      data.plk1.length === 2 &&
      data.plk3.length === 3 &&
      data.plk_serial.length === 2,
    {
      message: "پلاک باید ۸ رقم باشد",
    },
  );

export const finesPhotoSchema = z
  .object({
    warning_id: generateDigit(12, "validation.serialNo"),
    national_code: generateDigit(10, "validation.default").refine(
      isNationalIdValid,
      { message: "validation.nationalCode" },
    ),
    plk1: generateDigit(2, "validation.provinceCode"),
    plk2: z.string(),
    plk3: generateDigit(3, "validation.serial"),
    plk_serial: generateDigit(2, "validation.province"),
  })
  .refine(
    (data) =>
      data.plk1.length === 2 &&
      data.plk3.length === 3 &&
      data.plk_serial.length === 2,
    {
      message: "پلاک باید ۸ رقم باشد",
    },
  );

export const motorcycleFinesSchema = z.object({
  national_code: generateDigit(10, "validation.default").refine(
    isNationalIdValid,
    { message: "validation.nationalCode" },
  ),
  plk1: generateDigit(3, "validation.default"),
  plk2: generateDigit(5, "validation.default"),
});

export const pelakYabSchema = z.object({
  province: generateDigit(2, "validation.provinceCode"),
  type: z.string(),
});

export const plateInquirySchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
});

export const inquiryValiditySchema = z.object({
  code: z.string(),
});

export const passportStatusInquirySchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
});

export const sanaInquirySchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
  mobile_number: phoneNumberValidation,
});

export const foreignersInquirySchema = z.object({
  fida: generateDigit(12, "کد فیدا باید 12 رقم باشد"),
  birth_date: z.string().min(1, "validation.default"),
});

export const identityInquirySchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
  birth_day: z.string(),
});
export const negativePointsInquirySchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
  license_number: generateDigit(10),
});
export const drivingLicenseStatusInquirySchema = z.object({
  // The provider accepts a ten-digit national ID without checksum verification.
  national_code: generateDigit(10, "validation.invalid.invalidNationalCode"),
  mobileNumber: phoneNumberValidation,
});
export const bankValidationSchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
});
export const bankValidationSendOtpSchema = z.object({
  national_code: z.string().refine(isNationalIdValid, {
    message: "validation.invalid.invalidNationalCode",
  }),
  mobile_number: phoneNumberValidation,
});
export const bankValidationVerifyOtpSchema = z.object({
  otp: z.string().length(5, "کد باید 5 رقم باشد"),
  reference_id: z.string(),
});
export const corporateIdentitySchema = z.object({
  code: generateDigit(10, "شناسه صنفی باید 10 رقم باشد"),
});

export const chequeInquirySchema = z
  .object({
    nationalCode: z.string().optional(),
    nationalId: z.string().optional(),
    role: z.string().min(1),
    sayad_id: z.string(),
  })
  .refine(
    (data) => {
      // For individual (role "1"), nationalCode is required and must be 10 digits
      if (data.role === "1") {
        // Only validate if user has entered something
        if (!data.nationalCode || data.nationalCode.trim() === "") {
          return true; // Don't show error for empty field initially
        }
        return data.nationalCode.length === 10 && isNationalIdValid;
      }
      // For legal entity (role "2"), nationalId is required and must be 10 digits
      if (data.role === "2") {
        // Only validate if user has entered something
        if (!data.nationalId || data.nationalId.trim() === "") {
          return true; // Don't show error for empty field initially
        }
        return data.nationalId.length === 10 && /^\d+$/.test(data.nationalId);
      }
      return true;
    },
    {
      message: "validation.nationalCode",
      path: ["nationalCode"],
    },
  )
  .refine(
    (data) => {
      // For legal entity (role "2"), nationalId is required and must be 10 digits
      if (data.role === "2") {
        // Only validate if user has entered something
        if (!data.nationalId || data.nationalId.trim() === "") {
          return true; // Don't show error for empty field initially
        }
        return data.nationalId.length === 10 && /^\d+$/.test(data.nationalId);
      }
      return true;
    },
    {
      message: "validation.nationalId",
      path: ["nationalId"],
    },
  );

export const chequeV2InquirySchema = z.object({
  sayad_id: generateDigit(16, "validation.invalid.chequeSayyadId"),
});

export const chequeTransfersChainInquirySchema = z.object({
  sayad_id: generateDigit(16, "validation.invalid.chequeSayyadId"),
  cheque_type: z.string(),
  national_code: z.string().min(1, "validation.invalid.nationalCodeId"),
});

export const postalTrackingSchema = z.object({
  tracking_code: generateDigit(24, "validation.invalid.trackingCode"),
});

export const companyNewspaperInquirySchema = z.object({
  national_code: z.string(),
});

export const billInquirySchema = z.object({
  bill_id: z.string(),
});

export const gasBillInquirySchema = z
  .object({
    bill_id: z.string().optional(),
    subscription: z.string().optional(),
  })
  .refine((data) => data.bill_id || data.subscription, {
    message: "یکی از فیلدها الزامی است",
    path: ["bill_id"], // or show error on whichever field is active
  });

export const gasBillInfoInquirySchema = z.object({
  subscription: z.string(),
});
export const mtnBillInquirySchema = z.object({
  number: z.string(),
  type: z.string(),
});
